import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import {
  Typography,
  Table,
  Input,
  Select,
  Tag,
  Badge,
  Drawer,
  Modal,
  Form,
  Switch,
  Descriptions,
  Space,
  Button,
  message,
  Tooltip,
  Tabs,
  Alert,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  UserAddOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  TeamOutlined,
  SolutionOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { userApi, UserFilters, CreateUserData, CreateUserResponse } from '@/api/userApi';
import { registrationApi, RegistrationRequestDto, RegistrationStatus } from '@/api/registrationApi';
import { roleRequestApi, RoleRequestDto, RoleRequestStatus } from '@/api/roleRequestApi';
import { User, UserRole } from '@/types/common';
import PageHeader from '@/components/ui/PageHeader';
import EmptyState from '@/components/ui/EmptyState';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

dayjs.extend(relativeTime);

const { Text } = Typography;

const ROLE_COLORS: Record<UserRole, string> = {
  CEO: 'gold',
  BD_MANAGER: 'blue',
  ENTERPRISE_ARCHITECT: 'purple',
  CLOUD_ARCHITECT: 'cyan',
  OPPORTUNITY_FINDER: 'green',
  REQUIREMENT_RESOURCING_MGR: 'orange',
  TECHNICAL_WRITER: 'magenta',
  QC_LEAD: 'volcano',
  CONTRIBUTOR: 'default',
  ADMIN: 'red',
};

const ROLE_LABELS: Record<UserRole, string> = {
  CEO: 'CEO',
  BD_MANAGER: 'BD Manager',
  ENTERPRISE_ARCHITECT: 'Enterprise Architect',
  CLOUD_ARCHITECT: 'Cloud Architect',
  OPPORTUNITY_FINDER: 'Opportunity Finder',
  REQUIREMENT_RESOURCING_MGR: 'Resourcing Manager',
  TECHNICAL_WRITER: 'Technical Writer',
  QC_LEAD: 'QC Lead',
  CONTRIBUTOR: 'Contributor',
  ADMIN: 'Admin',
};

const ALL_ROLES: UserRole[] = [
  'CEO',
  'BD_MANAGER',
  'ENTERPRISE_ARCHITECT',
  'CLOUD_ARCHITECT',
  'OPPORTUNITY_FINDER',
  'REQUIREMENT_RESOURCING_MGR',
  'TECHNICAL_WRITER',
  'QC_LEAD',
  'CONTRIBUTOR',
  'ADMIN',
];

const STATUS_COLORS: Record<RegistrationStatus, string> = {
  PENDING: 'orange',
  APPROVED: 'success',
  REJECTED: 'error',
};

// ─── Users Tab ───────────────────────────────────────────────────────────────

const UsersTab = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState<UserFilters>({ page: 0, size: 10, isActive: true });
  const [searchText, setSearchText] = useState('');
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [createdUserResult, setCreatedUserResult] = useState<CreateUserResponse | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm] = Form.useForm();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await userApi.getAll(filters);
      setUsers(response.content);
      setTotalElements(response.totalElements);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchText || undefined, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setFilters((prev) => ({
      ...prev,
      page: (pagination.current || 1) - 1,
      size: pagination.pageSize || 10,
    }));
  };

  const handleEditSubmit = async () => {
    if (!editingUser) return;
    try {
      const values = await editForm.validateFields();
      await userApi.updateUser(editingUser.id, values);
      message.success('User updated successfully');
      setEditModalOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch {
      message.error('Failed to update user');
    }
  };

  const [addSubmitting, setAddSubmitting] = useState(false);

  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();
      setAddSubmitting(true);
      const submitData: CreateUserData = {
        fullName: values.fullName,
        username: values.username,
        email: values.email,
        role: values.role,
        isActive: values.isActive,
        reasonForAccess: values.reasonForAccess,
      };
      const result = await userApi.createUser(submitData);
      setAddModalOpen(false);
      addForm.resetFields();
      setCreatedUserResult(result);
      fetchUsers();
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      message.error(backendMsg || 'Failed to create user');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleDelete = (user: User) => {
    Modal.confirm({
      title: 'Deactivate User',
      content: `Are you sure you want to deactivate "${user.fullName}"? They will no longer be able to log in, but their name will remain visible in workflow history.`,
      okText: 'Deactivate',
      okType: 'danger',
      onOk: async () => {
        try {
          await userApi.deleteUser(user.id);
          message.success('User deactivated successfully');
          fetchUsers();
        } catch {
          message.error('Failed to deactivate user');
        }
      },
    });
  };

  const handlePermanentDelete = (user: User) => {
    Modal.confirm({
      title: 'Permanently Delete User',
      content: `Are you sure you want to permanently delete "${user.fullName}"? This action cannot be undone. The user will be removed from the users list entirely.`,
      okText: 'Permanently Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await userApi.permanentDeleteUser(user.id);
          message.success('User permanently deleted');
          fetchUsers();
        } catch {
          message.error('Failed to permanently delete user');
        }
      },
    });
  };

  const columns: ColumnsType<User> = [
    { title: 'Full Name', dataIndex: 'fullName', key: 'fullName', sorter: (a, b) => a.fullName.localeCompare(b.fullName) },
    { title: 'Username', dataIndex: 'username', key: 'username' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Role', dataIndex: 'role', key: 'role',
      render: (role: UserRole) => <Tag color={ROLE_COLORS[role]}>{ROLE_LABELS[role] || role}</Tag>,
    },
    {
      title: 'Secondary Role', dataIndex: 'secondaryRole', key: 'secondaryRole',
      render: (role?: UserRole) => role
        ? <Tag color={ROLE_COLORS[role]}>{ROLE_LABELS[role] || role}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status', dataIndex: 'isActive', key: 'isActive',
      render: (isActive: boolean) => (
        <Badge status={isActive ? 'success' : 'default'} text={isActive ? 'Active' : 'Inactive'} />
      ),
    },
    {
      title: 'Created', dataIndex: 'createdAt', key: 'createdAt',
      render: (date: string) => (date ? dayjs(date).format('MMM DD, YYYY') : '-'),
    },
    {
      title: 'Actions', key: 'actions', width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="View"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedUser(record); setViewDrawerOpen(true); }} /></Tooltip>
          <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditingUser(record); editForm.setFieldsValue({ role: record.role, secondaryRole: record.secondaryRole || undefined, isActive: record.isActive }); setEditModalOpen(true); }} /></Tooltip>
          <Tooltip title={record.isActive ? 'Deactivate' : 'Already deactivated'}><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} disabled={!record.isActive} /></Tooltip>
          {!record.isActive && (
            <Tooltip title="Permanently Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ color: 'var(--color-error)' }} onClick={() => handlePermanentDelete(record)} /></Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Resolve the current status selection for quick-filter chips.
  const statusSelection: 'ACTIVE' | 'INACTIVE' | 'ALL' =
    filters.isActive === undefined ? 'ALL' : filters.isActive ? 'ACTIVE' : 'INACTIVE';
  const setStatusSelection = (v: 'ACTIVE' | 'INACTIVE' | 'ALL') =>
    setFilters((p) => ({ ...p, isActive: v === 'ALL' ? undefined : v === 'ACTIVE', page: 0 }));
  const STATUS_CHIPS: { value: 'ACTIVE' | 'INACTIVE' | 'ALL'; label: string }[] = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'ALL', label: 'All' },
  ];

  return (
    <>
      <div className="ui-toolbar">
        <Input
          placeholder="Search users…"
          prefix={<SearchOutlined style={{ color: 'var(--color-text-tertiary)' }} />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ maxWidth: 260 }}
          allowClear
        />

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className="ui-chip ui-focusable"
              data-active={statusSelection === chip.value}
              onClick={() => setStatusSelection(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1 }} />

        <Select placeholder="Role" defaultValue="ALL" style={{ width: 170 }} onChange={(v) => setFilters((p) => ({ ...p, role: v === 'ALL' ? undefined : v as UserRole, page: 0 }))}>
          <Select.Option value="ALL">All Roles</Select.Option>
          {ALL_ROLES.map((r) => <Select.Option key={r} value={r}>{ROLE_LABELS[r]}</Select.Option>)}
        </Select>
        <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchUsers} /></Tooltip>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>Add User</Button>
      </div>

      <div className="ui-surface ui-animate-in" style={{ overflow: 'hidden' }}>
        <Table columns={columns} dataSource={users} rowKey="id" loading={loading}
          pagination={{ current: (filters.page || 0) + 1, pageSize: filters.size || 10, total: totalElements, showSizeChanger: true, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} users` }}
          onChange={handleTableChange}
          locale={{
            emptyText: loading ? <span /> : (
              <EmptyState
                icon={<TeamOutlined />}
                title={searchText ? 'No matching users' : 'No users found'}
                description={searchText ? 'Try a different search term or adjust the filters.' : 'Add a user to get started.'}
                action={!searchText ? (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>Add User</Button>
                ) : undefined}
              />
            ),
          }}
        />
      </div>

      {/* View Drawer */}
      <Drawer title="User Details" placement="right" width={480} open={viewDrawerOpen} onClose={() => { setViewDrawerOpen(false); setSelectedUser(null); }}>
        {selectedUser && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{selectedUser.id}</Descriptions.Item>
            <Descriptions.Item label="Keycloak ID">{selectedUser.keycloakId}</Descriptions.Item>
            <Descriptions.Item label="Full Name">{selectedUser.fullName}</Descriptions.Item>
            <Descriptions.Item label="Username">{selectedUser.username || '-'}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedUser.email}</Descriptions.Item>
            <Descriptions.Item label="Role"><Tag color={ROLE_COLORS[selectedUser.role]}>{ROLE_LABELS[selectedUser.role] || selectedUser.role}</Tag></Descriptions.Item>
            <Descriptions.Item label="Secondary Role">{selectedUser.secondaryRole ? <Tag color={ROLE_COLORS[selectedUser.secondaryRole]}>{ROLE_LABELS[selectedUser.secondaryRole] || selectedUser.secondaryRole}</Tag> : '—'}</Descriptions.Item>
            <Descriptions.Item label="Status"><Badge status={selectedUser.isActive ? 'success' : 'default'} text={selectedUser.isActive ? 'Active' : 'Inactive'} /></Descriptions.Item>
            <Descriptions.Item label="Created">{selectedUser.createdAt ? dayjs(selectedUser.createdAt).format('MMM DD, YYYY HH:mm') : '-'}</Descriptions.Item>
            <Descriptions.Item label="Updated">{selectedUser.updatedAt ? dayjs(selectedUser.updatedAt).format('MMM DD, YYYY HH:mm') : '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      {/* Add User Modal */}
      <Modal title={<Space><UserAddOutlined /><span>Add User</span></Space>} open={addModalOpen} onOk={handleAddSubmit} onCancel={() => { setAddModalOpen(false); addForm.resetFields(); }} okText="Create" confirmLoading={addSubmitting}>
        <Form form={addForm} layout="vertical" initialValues={{ isActive: true }}>
          <Form.Item name="fullName" label="Full Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Username is required' },
              { min: 3, message: 'Username must be at least 3 characters' },
              { pattern: /^[a-zA-Z0-9._-]+$/, message: 'Only letters, numbers, dots, hyphens and underscores' },
            ]}
            hasFeedback
            validateFirst
            validateTrigger="onBlur"
          >
            <Input placeholder="Unique login username" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true }, { type: 'email' }]}><Input /></Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select>{ALL_ROLES.map((r) => <Select.Option key={r} value={r}>{ROLE_LABELS[r]}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="reasonForAccess" label="Reason for Access">
            <Input.TextArea rows={3} placeholder="Why does this user need access?" maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
        </Form>
        <Alert type="info" showIcon message="A temporary password will be generated and emailed to the user. They will be required to change it on first login." style={{ marginTop: 8 }} />
      </Modal>

      {/* Created User Result Modal */}
      <Modal
        title="User Created Successfully"
        open={!!createdUserResult}
        onOk={() => setCreatedUserResult(null)}
        onCancel={() => setCreatedUserResult(null)}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText="Done"
      >
        {createdUserResult && (
          <>
            <Alert type="success" showIcon message="Account created and credentials emailed to the user." style={{ marginBottom: 16 }} />
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Full Name">{createdUserResult.fullName}</Descriptions.Item>
              <Descriptions.Item label="Username">{createdUserResult.username || createdUserResult.email}</Descriptions.Item>
              <Descriptions.Item label="Email">{createdUserResult.email}</Descriptions.Item>
              <Descriptions.Item label="Role"><Tag color={ROLE_COLORS[createdUserResult.role]}>{ROLE_LABELS[createdUserResult.role]}</Tag></Descriptions.Item>
              <Descriptions.Item label="Temporary Password">
                <Space>
                  <Text code copyable>{createdUserResult.tempPassword}</Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>
            <Alert type="warning" showIcon message="This temporary password is shown only once. The user must change it on first login." style={{ marginTop: 16 }} />
          </>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal title="Edit User" open={editModalOpen} onOk={handleEditSubmit} onCancel={() => { setEditModalOpen(false); setEditingUser(null); editForm.resetFields(); }} okText="Save">
        {editingUser && (
          <>
            <Descriptions column={1} size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Name">{editingUser.fullName}</Descriptions.Item>
              <Descriptions.Item label="Email">{editingUser.email}</Descriptions.Item>
            </Descriptions>
            <Form form={editForm} layout="vertical">
              <Form.Item name="role" label="Role" rules={[{ required: true }]}>
                <Select>{ALL_ROLES.map((r) => <Select.Option key={r} value={r}>{ROLE_LABELS[r]}</Select.Option>)}</Select>
              </Form.Item>
              <Form.Item name="secondaryRole" label="Secondary Role">
                <Select allowClear placeholder="None">
                  {ALL_ROLES.map((r) => <Select.Option key={r} value={r}>{ROLE_LABELS[r]}</Select.Option>)}
                </Select>
              </Form.Item>
              <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </>
  );
};

// ─── Registration Requests Tab ───────────────────────────────────────────────

const RegistrationRequestsTab = ({ onCountChange }: { onCountChange: (n: number) => void }) => {
  const [requests, setRequests] = useState<RegistrationRequestDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RegistrationStatus | undefined>(undefined);

  // Approve modal
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState<RegistrationRequestDto | null>(null);
  const [approveRole, setApproveRole] = useState<string>('');
  const [approving, setApproving] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<boolean>(true);
  const [keycloakRoleAssigned, setKeycloakRoleAssigned] = useState<boolean>(true);

  // Reject modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<RegistrationRequestDto | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await registrationApi.list({ status: statusFilter, page, size: 20 });
      const data = res.data.data;
      setRequests(data.content);
      setTotal(data.totalElements);
    } catch {
      message.error('Failed to load registration requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  const refreshCount = useCallback(async () => {
    try {
      const res = await registrationApi.getPendingCount();
      onCountChange(res.data.data.count);
    } catch {
      // silent
    }
  }, [onCountChange]);

  useEffect(() => {
    fetchRequests();
    refreshCount();
  }, [fetchRequests, refreshCount]);

  const openApprove = (req: RegistrationRequestDto) => {
    setApproveTarget(req);
    setApproveRole(req.requestedRole || 'OPPORTUNITY_FINDER');
    setTempPassword(null);
    setEmailSent(true);
    setKeycloakRoleAssigned(true);
    setApproveModalOpen(true);
  };

  const handleApprove = async () => {
    if (!approveTarget?.id) return;
    setApproving(true);
    try {
      const res = await registrationApi.approve(approveTarget.id, approveRole);
      setTempPassword(res.data.data.tempPassword || null);
      setEmailSent(res.data.data.emailSent !== false);
      setKeycloakRoleAssigned(res.data.data.keycloakRoleAssigned !== false);
      fetchRequests();
      refreshCount();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to approve request');
      setApproveModalOpen(false);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget?.id || !rejectReason.trim()) return;
    setRejecting(true);
    try {
      await registrationApi.reject(rejectTarget.id, rejectReason);
      message.success('Request rejected');
      setRejectModalOpen(false);
      setRejectTarget(null);
      setRejectReason('');
      fetchRequests();
      refreshCount();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to reject request');
    } finally {
      setRejecting(false);
    }
  };

  const handleDeleteRequest = (req: RegistrationRequestDto) => {
    Modal.confirm({
      title: 'Delete Registration Request',
      content: `Are you sure you want to delete the request from "${req.fullName}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await registrationApi.delete(req.id!);
          message.success('Registration request deleted');
          fetchRequests();
          refreshCount();
        } catch {
          message.error('Failed to delete registration request');
        }
      },
    });
  };

  const columns: ColumnsType<RegistrationRequestDto> = [
    { title: 'Full Name', dataIndex: 'fullName', key: 'fullName' },
    { title: 'Username', dataIndex: 'username', key: 'username' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Requested Role', dataIndex: 'requestedRole', key: 'requestedRole',
      render: (role?: string) => role
        ? <Tag color={ROLE_COLORS[role as UserRole] || 'default'}>{ROLE_LABELS[role as UserRole] || role}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Message', dataIndex: 'message', key: 'message', width: 220,
      render: (msg?: string) => msg
        ? <Tooltip title={msg}><Text style={{ fontSize: 12 }}>{msg.length > 60 ? msg.slice(0, 60) + '…' : msg}</Text></Tooltip>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Submitted', dataIndex: 'createdAt', key: 'createdAt',
      render: (d?: string) => d ? dayjs(d).fromNow() : '-',
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: RegistrationStatus) => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_, record) => (
        <Space>
          {record.status === 'PENDING' ? (
            <>
              <Tooltip title="Approve">
                <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => openApprove(record)}>Approve</Button>
              </Tooltip>
              <Tooltip title="Reject">
                <Button danger size="small" icon={<CloseOutlined />} onClick={() => { setRejectTarget(record); setRejectReason(''); setRejectModalOpen(true); }}>Reject</Button>
              </Tooltip>
            </>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.status === 'APPROVED' ? `Approved by ${record.reviewedByName || '—'}` : `Rejected by ${record.reviewedByName || '—'}`}
            </Text>
          )}
          <Tooltip title="Delete">
            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteRequest(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const REG_STATUS_CHIPS: { value: RegistrationStatus | 'ALL'; label: string }[] = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'ALL', label: 'All' },
  ];

  return (
    <>
      <div className="ui-toolbar">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {REG_STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className="ui-chip ui-focusable"
              data-active={(statusFilter ?? 'ALL') === chip.value}
              onClick={() => { setStatusFilter(chip.value === 'ALL' ? undefined : chip.value as RegistrationStatus); setPage(0); }}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={() => { fetchRequests(); refreshCount(); }} /></Tooltip>
      </div>

      <div className="ui-surface ui-animate-in" style={{ overflow: 'hidden' }}>
        <Table columns={columns} dataSource={requests} rowKey="id" loading={loading}
          pagination={{ current: page + 1, pageSize: 20, total, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} requests`, onChange: (p) => setPage(p - 1) }}
          locale={{
            emptyText: loading ? <span /> : (
              <EmptyState
                icon={<SolutionOutlined />}
                title="No registration requests"
                description="Pending access requests from new users will appear here."
              />
            ),
          }}
        />
      </div>

      {/* Approve Modal */}
      <Modal
        title={<Space><CheckOutlined style={{ color: 'var(--color-success)' }} /><span>Approve Access Request</span></Space>}
        open={approveModalOpen}
        footer={tempPassword ? [
          <Button key="close" type="primary" onClick={() => { setApproveModalOpen(false); setTempPassword(null); message.success('User account created successfully'); }}>Done</Button>
        ] : [
          <Button key="cancel" onClick={() => setApproveModalOpen(false)}>Cancel</Button>,
          <Button key="approve" type="primary" loading={approving} onClick={handleApprove}>Approve & Create Account</Button>,
        ]}
        onCancel={() => { if (!tempPassword) setApproveModalOpen(false); }}
        closable={!approving}
      >
        {tempPassword ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type="success"
              message="Account created successfully!"
              description={`A new account has been created for ${approveTarget?.fullName}. The temporary password is shown below — save it now.`}
              showIcon
            />
            {!emailSent && (
              <Alert
                type="error"
                showIcon
                message="Credential email could not be sent"
                description="The system was unable to email the login credentials. Please share the username and temporary password below with the user manually."
              />
            )}
            {!keycloakRoleAssigned && (
              <Alert
                type="warning"
                showIcon
                message="Role could not be assigned in Keycloak"
                description="The assigned role may not exist in Keycloak. The user can still log in and use the system (the role is stored in the database), but you may need to create the role in the Keycloak admin console for full SSO role sync."
              />
            )}
            <Alert
              type="warning"
              showIcon
              message="Temporary Password — shown only once"
              description={
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input.Password
                    value={tempPassword}
                    readOnly
                    addonAfter={
                      <CopyOutlined
                        style={{ cursor: 'pointer' }}
                        onClick={() => { navigator.clipboard.writeText(tempPassword); message.success('Copied!'); }}
                      />
                    }
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    The user will be required to change this password on first login.
                  </Text>
                </Space>
              }
            />
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            {approveTarget && (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Full Name">{approveTarget.fullName}</Descriptions.Item>
                <Descriptions.Item label="Username">{approveTarget.username}</Descriptions.Item>
                <Descriptions.Item label="Email">{approveTarget.email}</Descriptions.Item>
                {approveTarget.message && <Descriptions.Item label="Message">{approveTarget.message}</Descriptions.Item>}
              </Descriptions>
            )}
            <div style={{ marginTop: 16 }}>
              <Text strong>Assign Role:</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={approveRole}
                onChange={setApproveRole}
              >
                {ALL_ROLES.filter(r => r !== 'ADMIN').map((r) => (
                  <Select.Option key={r} value={r}>{ROLE_LABELS[r]}</Select.Option>
                ))}
              </Select>
            </div>
          </Space>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal
        title={<Space><CloseOutlined style={{ color: 'var(--color-error)' }} /><span>Reject Access Request</span></Space>}
        open={rejectModalOpen}
        onOk={handleReject}
        onCancel={() => { setRejectModalOpen(false); setRejectTarget(null); }}
        okText="Reject Request"
        okButtonProps={{ danger: true, loading: rejecting, disabled: !rejectReason.trim() }}
      >
        {rejectTarget && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>Rejecting access request from <strong>{rejectTarget.fullName}</strong> ({rejectTarget.email}).</Text>
            <div style={{ marginTop: 8 }}>
              <Text strong>Reason for rejection:</Text>
              <Input.TextArea
                style={{ marginTop: 8 }}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this request is being rejected..."
                rows={3}
                maxLength={500}
                showCount
              />
            </div>
          </Space>
        )}
      </Modal>
    </>
  );
};

// ─── Role Requests Tab ────────────────────────────────────────────────────────

const ROLE_REQUEST_STATUS_COLORS: Record<RoleRequestStatus, string> = {
  PENDING: 'orange',
  APPROVED: 'success',
  REJECTED: 'error',
};

const RoleRequestsTab = ({ onCountChange }: { onCountChange: (n: number) => void }) => {
  const [requests, setRequests] = useState<RoleRequestDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<RoleRequestStatus | undefined>(undefined);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<RoleRequestDto | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await roleRequestApi.list({ status: statusFilter, page, size: 20 });
      const data = res.data.data;
      setRequests(data.content);
      setTotal(data.totalElements);
    } catch {
      message.error('Failed to load role requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page]);

  const refreshCount = useCallback(async () => {
    try {
      const res = await roleRequestApi.getPendingCount();
      onCountChange(res.data.data.count);
    } catch { /* silent */ }
  }, [onCountChange]);

  useEffect(() => { fetchRequests(); refreshCount(); }, [fetchRequests, refreshCount]);

  const handleApprove = async (req: RoleRequestDto) => {
    try {
      await roleRequestApi.approve(req.id!);
      message.success('Role request approved');
      fetchRequests();
      refreshCount();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to approve request');
    }
  };

  const handleReject = async () => {
    if (!rejectTarget?.id || !rejectReason.trim()) return;
    setRejecting(true);
    try {
      await roleRequestApi.reject(rejectTarget.id, rejectReason);
      message.success('Role request rejected');
      setRejectModalOpen(false);
      setRejectTarget(null);
      setRejectReason('');
      fetchRequests();
      refreshCount();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to reject request');
    } finally {
      setRejecting(false);
    }
  };

  const columns: ColumnsType<RoleRequestDto> = [
    { title: 'Full Name', dataIndex: 'userFullName', key: 'userFullName' },
    { title: 'Email', dataIndex: 'userEmail', key: 'userEmail' },
    {
      title: 'Current Role', dataIndex: 'currentRole', key: 'currentRole',
      render: (role?: string) => role
        ? <Tag color={ROLE_COLORS[role as UserRole] || 'default'}>{ROLE_LABELS[role as UserRole] || role}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Requested Role', dataIndex: 'requestedRole', key: 'requestedRole',
      render: (role?: string) => role
        ? <Tag color={ROLE_COLORS[role as UserRole] || 'default'}>{ROLE_LABELS[role as UserRole] || role}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Reason', dataIndex: 'reason', key: 'reason', width: 200,
      render: (reason?: string) => reason
        ? <Tooltip title={reason}><Text style={{ fontSize: 12 }}>{reason.length > 50 ? reason.slice(0, 50) + '…' : reason}</Text></Tooltip>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Submitted', dataIndex: 'createdAt', key: 'createdAt',
      render: (d?: string) => d ? dayjs(d).fromNow() : '-',
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: RoleRequestStatus) => <Tag color={ROLE_REQUEST_STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 200,
      render: (_, record) => (
        <Space>
          {record.status === 'PENDING' ? (
            <>
              <Tooltip title="Approve">
                <Button type="primary" size="small" icon={<CheckOutlined />} onClick={() => handleApprove(record)}>Approve</Button>
              </Tooltip>
              <Tooltip title="Reject">
                <Button danger size="small" icon={<CloseOutlined />} onClick={() => { setRejectTarget(record); setRejectReason(''); setRejectModalOpen(true); }}>Reject</Button>
              </Tooltip>
            </>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.status === 'APPROVED' ? `Approved by ${record.reviewedByName || '—'}` : `Rejected by ${record.reviewedByName || '—'}`}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const ROLE_STATUS_CHIPS: { value: RoleRequestStatus | 'ALL'; label: string }[] = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' },
    { value: 'ALL', label: 'All' },
  ];

  return (
    <>
      <div className="ui-toolbar">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ROLE_STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className="ui-chip ui-focusable"
              data-active={(statusFilter ?? 'ALL') === chip.value}
              onClick={() => { setStatusFilter(chip.value === 'ALL' ? undefined : chip.value as RoleRequestStatus); setPage(0); }}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={() => { fetchRequests(); refreshCount(); }} /></Tooltip>
      </div>

      <div className="ui-surface ui-animate-in" style={{ overflow: 'hidden' }}>
        <Table columns={columns} dataSource={requests} rowKey="id" loading={loading}
          pagination={{ current: page + 1, pageSize: 20, total, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} requests`, onChange: (p) => setPage(p - 1) }}
          locale={{
            emptyText: loading ? <span /> : (
              <EmptyState
                icon={<SafetyCertificateOutlined />}
                title="No role requests"
                description="Requests from users to change their role will appear here."
              />
            ),
          }}
        />
      </div>

      {/* Reject Modal */}
      <Modal
        title={<Space><CloseOutlined style={{ color: 'var(--color-error)' }} /><span>Reject Role Request</span></Space>}
        open={rejectModalOpen}
        onOk={handleReject}
        onCancel={() => { setRejectModalOpen(false); setRejectTarget(null); }}
        okText="Reject Request"
        okButtonProps={{ danger: true, loading: rejecting, disabled: !rejectReason.trim() }}
      >
        {rejectTarget && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              Rejecting role request from <strong>{rejectTarget.userFullName}</strong> for the{' '}
              <Tag color={ROLE_COLORS[rejectTarget.requestedRole as UserRole] || 'default'}>
                {ROLE_LABELS[rejectTarget.requestedRole as UserRole] || rejectTarget.requestedRole}
              </Tag> role.
            </Text>
            <div style={{ marginTop: 8 }}>
              <Text strong>Reason for rejection:</Text>
              <Input.TextArea
                style={{ marginTop: 8 }}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this request is being rejected..."
                rows={3}
                maxLength={500}
                showCount
              />
            </div>
          </Space>
        )}
      </Modal>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const UserManagementPage = () => {
  const [pendingRegCount, setPendingRegCount] = useState(0);
  const [pendingRoleCount, setPendingRoleCount] = useState(0);
  const [searchParams] = useSearchParams();
  const { hasRole } = useAuth();
  const isAdmin = hasRole('ADMIN');
  const defaultTab = searchParams.get('tab') === 'registrations' && isAdmin
    ? 'registrations'
    : searchParams.get('tab') === 'role-requests' && isAdmin
      ? 'role-requests'
      : 'users';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const tabItems = useMemo(() => {
    const items = [
      {
        key: 'users',
        label: 'Users',
        children: <UsersTab />,
      },
    ];
    if (isAdmin) {
      items.push({
        key: 'registrations',
        label: (
          <Badge count={pendingRegCount} size="small" offset={[6, -2]}>
            Registration Requests
          </Badge>
        ) as React.ReactNode as string,
        children: <RegistrationRequestsTab onCountChange={setPendingRegCount} />,
      });
      items.push({
        key: 'role-requests',
        label: (
          <Badge count={pendingRoleCount} size="small" offset={[6, -2]}>
            Role Requests
          </Badge>
        ) as React.ReactNode as string,
        children: <RoleRequestsTab onCountChange={setPendingRoleCount} />,
      });
    }
    return items;
  }, [isAdmin, pendingRegCount, pendingRoleCount]);

  const pendingTotal = pendingRegCount + pendingRoleCount;

  return (
    <div>
      <PageHeader
        title="User Management"
        subtitle={
          isAdmin
            ? `${pendingTotal} pending ${pendingTotal === 1 ? 'request' : 'requests'} awaiting review`
            : 'Manage users and their access'
        }
      />
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </div>
  );
};

export default UserManagementPage;
